/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, HFlow, Icon, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Form, SubmitButton, TextAreaField, TextField } from 'components/form'
import { ProcedimentoOdontoSelectField } from 'components/form/field/select/ProcedimentoOdontoSelectField'
import { PopperButton, PopperControls } from 'components/popper'
import { FormApi } from 'final-form'
import { ParteBucalEnum } from 'graphql/types.generated'
import { useMemo } from 'react'
import { metaPath } from 'util/metaPath'
import { createValidator, maxLength, required } from 'util/validation'

import { EvolucaoDenteSupranumerarioModel, trimSupranumeriosObservacao } from './DentesSupranumerariosField'

export interface AdicionarDenteSupranumerarioPopupProps {
  cidadaoDataNascimento: LocalDate
  onSubmit(values: EvolucaoDenteSupranumerarioModel): void
}

export const dentesSupranumeriosFormValidator = createValidator<EvolucaoDenteSupranumerarioModel>({
  local: [required, maxLength(200)],
  procedimentos: [required],
  observacao: [maxLength(400)],
})

const path = metaPath<EvolucaoDenteSupranumerarioModel>()

export const AdicionarDenteSupranumerarioPopup = (props: AdicionarDenteSupranumerarioPopupProps) => {
  const { cidadaoDataNascimento, onSubmit } = props

  const theme = useTheme()
  const classes = useMemo(() => styles(theme), [theme])

  const renderPopper = (controls: PopperControls) => {
    const handleSubmit = (values: EvolucaoDenteSupranumerarioModel, form: FormApi) => {
      onSubmit(trimSupranumeriosObservacao(values))

      setTimeout(() => {
        resetForm(form)
        controls.close()
      })
    }

    const handleCancel = (form: FormApi) => {
      if (form.getState().dirty) resetForm(form)
      controls.close()
    }

    return (
      <div css={classes.popper}>
        <Form<EvolucaoDenteSupranumerarioModel>
          validate={dentesSupranumeriosFormValidator}
          onSubmit={handleSubmit}
          render={(formProps) => {
            return (
              <VFlow>
                <TextField name={path.local} label='Local' maxLength={200} required />

                <ProcedimentoOdontoSelectField
                  name={path.procedimentos}
                  label='Procedimentos (Evolução odontológica)'
                  multiple
                  dataNascimento={cidadaoDataNascimento}
                  parteBucal={ParteBucalEnum.DENTE_SUPRANUMERARIO}
                  required
                />

                <TextAreaField name={path.observacao} label='Observações' maxLength={400} style={{ resize: 'none' }} />

                <HFlow justifyContent='flex-end'>
                  <Button size='small' onClick={() => handleCancel(formProps.form)}>
                    Cancelar
                  </Button>

                  <SubmitButton size='small' kind='primary' handleSubmit={formProps.handleSubmit}>
                    Adicionar
                  </SubmitButton>
                </HFlow>
              </VFlow>
            )
          }}
        />
      </div>
    )
  }

  return (
    <PopperButton kind='primary' size='small' skin='outline' placement='top-start' renderPopper={renderPopper}>
      <Icon icon='plus' style={{ marginRight: '0.25rem' }} />
      <Text color='inherit'>Adicionar procedimentos em dentes supranumerários</Text>
    </PopperButton>
  )
}

const styles = (theme: Theme) => ({
  popper: css`
    width: 27.625rem;
    max-height: 27.625rem;
    margin: 0.5rem 0;
    border-radius: ${theme.radius.popper}px;
    background: ${theme.pallete.primary.c100};
    box-shadow: ${theme.shadows.outer['40']};
    padding: 1rem;
    overflow-y: auto;
  `,
})

const resetForm = (form: FormApi) => {
  form.reset({})
  form.resetFieldState(path.local.absolutePath())
  form.resetFieldState(path.procedimentos.absolutePath())
  form.resetFieldState(path.observacao.absolutePath())
}
